import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { toastError, toastSuccess } from "@/lib/toast";
import { API_BASE_URL } from "@/lib/apiBase";

const TYPE_LABELS = {
  "work.mention": "Mentions",
  "work.assignee": "Assignments",
  "team.handoff": "Handoffs",
  "post.critical": "Critical posts",
  "channel.here": "@here in channels",
  "post.comment": "Comments on your posts",
  "comment.reply": "Replies to your comments",
  "post.like": "Likes on your posts",
  "invite.accepted": "Invite accepted",
  "team.join_request": "Team join requests",
  "partner.invite": "Partner invitations",
  "partner.accepted": "Partner accepted",
  "partner.ended": "Partnership ended",
  "org.profile_setup": "Org profile setup reminders",
};

/**
 * Per-org work-bell mute preferences (Profile → Workspace).
 */
const NotificationPrefsPanel = ({ accessToken, orgId, orgName }) => {
  const [muteableTypes, setMuteableTypes] = useState([]);
  const [mutedTypes, setMutedTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!accessToken || !orgId) {
      setMuteableTypes([]);
      setMutedTypes([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/notifications/preferences`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { org_id: orgId },
      });
      setMuteableTypes(
        Array.isArray(res.data?.muteable_types) ? res.data.muteable_types : []
      );
      setMutedTypes(
        Array.isArray(res.data?.muted_types) ? res.data.muted_types : []
      );
    } catch (err) {
      console.error("Failed to load notification preferences:", err);
      setError("Could not load notification preferences.");
      setMuteableTypes([]);
      setMutedTypes([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveMuted = async (nextMuted) => {
    if (!accessToken || !orgId || saving) return;
    const previous = mutedTypes;
    setMutedTypes(nextMuted);
    setSaving(true);
    setError(null);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/notifications/preferences`,
        { muted_types: nextMuted },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { org_id: orgId },
        }
      );
      setMutedTypes(
        Array.isArray(res.data?.muted_types) ? res.data.muted_types : nextMuted
      );
      if (Array.isArray(res.data?.muteable_types)) {
        setMuteableTypes(res.data.muteable_types);
      }
      toastSuccess("Notification preferences saved");
    } catch (err) {
      console.error("Failed to save notification preferences:", err);
      setMutedTypes(previous);
      toastError(err, "Failed to save notification preferences");
    } finally {
      setSaving(false);
    }
  };

  const mutedSet = new Set(mutedTypes);
  const allMuted =
    muteableTypes.length > 0 &&
    muteableTypes.every((t) => mutedSet.has(t));

  const toggleType = (type, enabled) => {
    // enabled checkbox = receive notifications = not muted
    const next = enabled
      ? mutedTypes.filter((t) => t !== type)
      : [...new Set([...mutedTypes, type])];
    saveMuted(next);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
        <CardDescription>
          {orgName
            ? `Choose which in-app alerts you get for ${orgName}. Defaults are all on. Channel and DM mute still lives on each conversation in Messages.`
            : "Choose a workspace to set notification preferences."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!orgId ? (
          <p className="text-sm text-muted-foreground">
            No workspace selected. Use the Workspace switcher at the top of the
            sidebar, then return here.
          </p>
        ) : loading ? (
          <p className="text-sm text-muted-foreground">Loading preferences…</p>
        ) : error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={saving || allMuted}
                onClick={() => saveMuted([...muteableTypes])}
              >
                Mute all
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={saving || mutedTypes.length === 0}
                onClick={() => saveMuted([])}
              >
                Unmute all
              </Button>
            </div>
            <ul className="divide-y divide-border">
              {muteableTypes.map((type) => {
                const receive = !mutedSet.has(type);
                const label = TYPE_LABELS[type] || type;
                const switchId = `notif-pref-${type}`;
                return (
                  <li
                    key={type}
                    className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
                  >
                    <label
                      htmlFor={switchId}
                      className="text-sm font-medium cursor-pointer"
                    >
                      {label}
                    </label>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-xs text-muted-foreground w-7 text-right">
                        {receive ? "On" : "Off"}
                      </span>
                      <Switch
                        id={switchId}
                        checked={receive}
                        disabled={saving}
                        onCheckedChange={(on) => toggleType(type, on)}
                        aria-label={label}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationPrefsPanel;
